import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import type { AreaChartElement } from "./catalog"

import {
  ChartTitle,
  colorForSeries,
  CHART_OUTLINE,
  computeSeriesStats,
  DEFAULT_YAXIS_WIDTH,
  formatTickNumber,
  renderSeriesStatsText,
  sharedTooltipProps,
  TIMESERIES_SYNC_ID,
} from "./chart-shared"
import { TileEmptyState } from "../components/tile-empty-state"

const LINE_WIDTH = 2.5

export function AreaChartComponent({ element }: { element: AreaChartElement }) {
  if (!Array.isArray(element.data) || element.data.length === 0) {
    return <TileEmptyState title={element.title} />
  }
  const showLegend = element.areas.length > 1
  const seriesStats = element.areas.map((area, i) => {
    const stats = computeSeriesStats(element.data, area.dataKey)
    return {
      key: area.dataKey,
      name: area.label ?? area.dataKey,
      color: colorForSeries(area.label ?? area.dataKey, i, area.color),
      stats,
    }
  })
  const showStatsStrip = seriesStats.some((s) => s.stats.last !== null)
  return (
    <div className="bg-card border-border rounded-xl border p-4 shadow-sm">
      <ChartTitle title={element.title} />
      <ResponsiveContainer height={280} width="100%">
        <AreaChart
          data={element.data}
          margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
          syncId={TIMESERIES_SYNC_ID}
        >
          <defs>
            {element.areas.map((area, i) => {
              const c = colorForSeries(area.label ?? area.dataKey, i, area.color)
              return (
                <linearGradient
                  key={area.dataKey}
                  id={`area-gradient-${i}`}
                  x1="0"
                  x2="0"
                  y1="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={c} stopOpacity={0.55} />
                  <stop offset="100%" stopColor={c} stopOpacity={0.05} />
                </linearGradient>
              )
            })}
          </defs>
          <CartesianGrid className="stroke-border" strokeDasharray="3 3" vertical={false} />
          <XAxis axisLine={false} className="text-xs" dataKey={element.xKey} tickLine={false} />
          <YAxis
            axisLine={false}
            className="text-xs"
            tickFormatter={formatTickNumber}
            tickLine={false}
            width={DEFAULT_YAXIS_WIDTH}
          />
          <Tooltip
            cursor={{ stroke: "hsl(var(--muted-foreground) / 0.3)", strokeWidth: 1 }}
            {...sharedTooltipProps}
          />
          {/* Recharts Legend hidden when our stats strip renders below — see chart-line.tsx for the rationale. */}
          {showLegend && !showStatsStrip && (
            <Legend iconType="circle" wrapperStyle={{ paddingTop: 8 }} />
          )}
          {/* Underlays first so they render behind the coloured strokes. Stacked series skip
              the underlay (a second Area with the same stackId would double the stack) and
              take the foreground outline on the series stroke instead. */}
          {element.areas
            .filter((area) => !area.stackId)
            .map((area) => (
              <Area
                key={`${area.dataKey}-underlay`}
                activeDot={false}
                dataKey={area.dataKey}
                dot={false}
                fill="none"
                isAnimationActive={false}
                legendType="none"
                stroke={CHART_OUTLINE}
                strokeWidth={LINE_WIDTH + 2}
                tooltipType="none"
                type="monotone"
              />
            ))}
          {element.areas.map((area, i) => (
            <Area
              key={area.dataKey}
              activeDot={{ r: 5, strokeWidth: 2 }}
              dataKey={area.dataKey}
              fill={`url(#area-gradient-${i})`}
              name={area.label ?? area.dataKey}
              stackId={area.stackId}
              stroke={
                area.stackId
                  ? CHART_OUTLINE
                  : colorForSeries(area.label ?? area.dataKey, i, area.color)
              }
              strokeWidth={area.stackId ? 1 : LINE_WIDTH}
              type="monotone"
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
      {showStatsStrip && (
        <div className="text-muted-foreground mt-2 flex flex-col gap-0.5 text-[11px] tabular-nums">
          {seriesStats
            .filter((s) => s.stats.last !== null)
            .map((s) => (
              <div key={s.key} className="flex items-center gap-2">
                <span
                  className="border-foreground inline-block size-2 shrink-0 rounded-full border"
                  style={{ backgroundColor: s.color }}
                />
                <span className="text-foreground font-medium">{s.name}</span>
                <span className="opacity-80">{renderSeriesStatsText(s.stats)}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  )
}

// eslint-disable-next-line import/no-default-export
export default AreaChartComponent
